import React, { useState, useCallback } from 'react';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button,
  Select,
  TextInput,
  toast
} from '@etherisc/ui-kit';
import { 
  DownloadIcon, 
  XIcon,
  CheckCircleIcon
} from 'lucide-react';
import { FilesService } from '../../services/climate/files/FilesService';
import type { 
  ChirpsDataRequestParams, 
  ChirpsDataRequestResponse 
} from '../../types/files';
import { ClimateApiError } from '../../services/climate/base/ClimateApiError';

interface ChirpsDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChirpsDownloadDialog({ 
  isOpen, 
  onClose
}: ChirpsDownloadDialogProps) {
  const [formData, setFormData] = useState<ChirpsDataRequestParams>({
    temporal_resolution: '',
    region: '',
    data_source: 'standard',
    quality: 'standard',
    format: '',
    years: ''
  });
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ChirpsDataRequestResponse | null>(null);

  const filesService = new FilesService();

  // Reset form when dialog opens/closes
  const resetForm = useCallback(() => {
    setFormData({
      temporal_resolution: '',
      region: '',
      data_source: 'standard',
      quality: 'standard',
      format: '',
      years: ''
    });
    setResponse(null);
    setLoading(false);
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof ChirpsDataRequestParams, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.temporal_resolution || !formData.region || !formData.data_source || !formData.quality || !formData.format || !formData.years) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate years format (single year or range)
    const yearsPattern = /^(\d{4})(-\d{4})?$/;
    if (!yearsPattern.test(formData.years)) {
      toast.error('Years must be a single year (e.g., 2018) or a range (e.g., 2018-2020)');
      return;
    }

    setLoading(true);
    
    try {
      const result = await filesService.requestChirpsData(formData);
      setResponse(result);
      toast.success('CHIRPS data request submitted successfully!');
    } catch (error) {
      console.error('Download request error:', error);
      
      let errorMessage = 'Failed to request CHIRPS data download';
      
      if (error instanceof ClimateApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle cases where error is an object but not an Error instance
        errorMessage = JSON.stringify(error);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData, filesService]);

  // Close dialog and reset
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-lg mx-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DownloadIcon className="h-5 w-5" />
                Download CHIRPS Files
              </CardTitle>
              <Button
                intent="outline"
                size="sm"
                onClick={handleClose}
                disabled={loading}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {response ? (
              // Success Response Display
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Request Submitted Successfully</span>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                  <div><strong>Job ID:</strong> {response.job_id}</div>
                  <div><strong>Status:</strong> {response.status}</div>
                  <div><strong>Message:</strong> {response.message}</div>
                  {response.estimated_completion_time && (
                    <div><strong>Estimated Completion:</strong> {response.estimated_completion_time}</div>
                  )}
                </div>
                
                <div className="text-sm text-gray-600">
                  You can monitor the progress in the Jobs section of the admin panel.
                </div>
                
                <Button
                  onClick={handleClose}
                  className="w-full"
                  intent="primary"
                >
                  Close
                </Button>
              </div>
            ) : (
              // Form
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Temporal Resolution */}
                <Select
                  label="Temporal Resolution *"
                  options={[
                    { value: '2-monthly', label: '2-monthly' },
                    { value: '3-monthly', label: '3-monthly' },
                    { value: '4-monthly', label: '4-monthly' },
                    { value: '5-monthly', label: '5-monthly' },
                    { value: '6-monthly', label: '6-monthly' },
                    { value: 'annual', label: 'Annual' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'dekads', label: 'Dekads' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'pentads', label: 'Pentads' }
                  ]}
                  value={formData.temporal_resolution}
                  onValueChange={(value) => handleFieldChange('temporal_resolution', value)}
                  placeholder="Select temporal resolution"
                  disabled={loading}
                />

                {/* Region */}
                <Select
                  label="Region *"
                  options={[
                    { value: 'global', label: 'Global' },
                    { value: 'africa', label: 'Africa' },
                    { value: 'latam', label: 'Latin America' }
                  ]}
                  value={formData.region}
                  onValueChange={(value) => handleFieldChange('region', value)}
                  placeholder="Select region"
                  disabled={loading}
                />

                {/* Data Source */}
                <Select
                  label="Data Source *"
                  options={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'ERA5', label: 'ERA5' },
                    { value: 'IMERGlate-v07', label: 'IMERG Late v0.7' }
                  ]}
                  value={formData.data_source}
                  onValueChange={(value) => handleFieldChange('data_source', value)}
                  placeholder="Select data source"
                  disabled={loading}
                />

                {/* Quality */}
                <Select
                  label="Quality *"
                  options={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'prelim', label: 'Preliminary' },
                    { value: 'final', label: 'Final' }
                  ]}
                  value={formData.quality}
                  onValueChange={(value) => handleFieldChange('quality', value)}
                  placeholder="Select quality"
                  disabled={loading}
                />

                {/* Format */}
                <Select
                  label="Format *"
                  options={[
                    { value: 'COG', label: 'COG' },
                    { value: 'GeoTIFF', label: 'GeoTIFF' },
                    { value: 'NetCDF', label: 'NetCDF' },
                    { value: 'PNG', label: 'PNG' }
                  ]}
                  value={formData.format}
                  onValueChange={(value) => handleFieldChange('format', value)}
                  placeholder="Select file format"
                  disabled={loading}
                />

                {/* Years */}
                <TextInput
                  label="Years *"
                  value={formData.years}
                  onChange={(e) => handleFieldChange('years', e.target.value)}
                  placeholder="e.g., 2018 or 2018-2020"
                  disabled={loading}
                  required
                />

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Note:</p>
                    <p>
                      Submitting this request will create a background job to prepare your CHIRPS data files. 
                      You can monitor the progress in the Jobs section.
                    </p>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    intent="outline"
                    onClick={handleClose}
                    disabled={loading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !formData.temporal_resolution || !formData.region || !formData.data_source || !formData.quality || !formData.format || !formData.years}
                    className="flex-1"
                    intent="primary"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <DownloadIcon className="h-4 w-4 mr-2" />
                        Start Download Job
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 